import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Chip,
  IconButton,
  Collapse,
  Snackbar,
  Alert,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { AutoTaskAuthContext } from "../Context/AutoTaskContext";
import axios from "axios";
import moment from "moment";

/* ------------------ Helpers ------------------ */

const getProgressColor = (percentage) => {
  if (percentage >= 80) return "success";
  if (percentage >= 60) return "warning";
  return "error";
};

/* ------------------ Component ------------------ */

const CeoDashboard = () => {
  const currentYear = moment().year();
  const [selectedMonth, setSelectedMonth] = useState(moment().month() + 1);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const [snackBarConfig, setSnackBarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const { userInfo } = useContext(AutoTaskAuthContext);
  const store_id = userInfo?.store_id;

  const [weeklyPerformance, setWeeklyPerformance] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [expandedTaskGroups, setExpandedTaskGroups] = useState([]);
  const [currentWeek, setCurrentWeek] = useState(moment().isoWeek());
  const [currentISOYear, setCurrentISOYear] = useState(moment().isoWeekYear());
  const [weekYearMap, setWeekYearMap] = useState({});

  const yearOptions = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);

  /* ------------------ Fetch Data ------------------ */

  useEffect(() => {
    const fetchWeeklyPerformanceData = async () => {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/ticket/getmonthlyperformance/${selectedMonth}/${selectedYear}/${store_id}`;
      try {
        const response = await axios.get(url);
        if (response?.data?.success) {
          setWeeklyPerformance(response.data);
          setWeeks(response.data.meta.weeks);
          setCurrentWeek(moment().isoWeek());
          setCurrentISOYear(moment().isoWeekYear());

          const startOfMonth = moment(
            `${selectedYear}-${selectedMonth}-01`
          ).startOf("month");
          const endOfMonth = moment(
            `${selectedYear}-${selectedMonth}-01`
          ).endOf("month");

          const weekMap = {};
          let cursor = startOfMonth.clone();
          while (cursor.isSameOrBefore(endOfMonth)) {
            weekMap[cursor.isoWeek()] = cursor.isoWeekYear();
            cursor.add(1, "day");
          }

          setWeekYearMap(weekMap);
        } else {
          setSnackBarConfig({
            open: true,
            message: "Failed to fetch performance data",
            severity: "error",
          });
        }
      } catch (err) {
        setSnackBarConfig({
          open: true,
          message: "Error fetching performance data",
          severity: "error",
        });
      }
    };

    fetchWeeklyPerformanceData();
  }, [store_id, selectedMonth, selectedYear]);

  /* ------------------ Calculations ------------------ */

  useEffect(() => {
    if (!weeklyPerformance || Object.keys(weekYearMap).length === 0) return;

    const updatedDepartments = weeklyPerformance.departments.map((dept) => {
      const pastWeeks = weeks.filter((week) => {
        const weekNumber = parseInt(week.key.replace("w", ""));
        const weekYear = weekYearMap[weekNumber];
        if (weekYear < currentISOYear) return true;
        if (weekYear > currentISOYear) return false;
        return weekNumber <= currentWeek;
      });

      const numberOfPastWeeks = pastWeeks.length;

      const processedTaskGroups = dept.task_groups.map((tg) => {
        const taskGroup_weekly_target = tg.users.reduce(
          (s, u) => s + u.weekly_target,
          0
        );

        const taskGroup_weekly_data = {};
        weeks.forEach((w) => {
          const wn = parseInt(w.key.replace("w", ""));
          const wy = weekYearMap[wn];
          const passed =
            wy < currentISOYear || (wy === currentISOYear && wn <= currentWeek);

          taskGroup_weekly_data[w.key] = passed
            ? tg.users.reduce(
                (s, u) =>
                  s +
                  (typeof u.weekly_data[w.key] === "number"
                    ? u.weekly_data[w.key]
                    : 0),
                0
              )
            : "-";
        });

        const taskGroup_monthly_target =
          taskGroup_weekly_target * numberOfPastWeeks;

        const taskGroup_monthly_completed = Object.values(
          taskGroup_weekly_data
        ).reduce((s, v) => s + (typeof v === "number" ? v : 0), 0);

        const taskGroup_monthly_percentage =
          taskGroup_monthly_target > 0
            ? Math.round(
                (taskGroup_monthly_completed / taskGroup_monthly_target) * 100
              )
            : 0;

        return {
          ...tg,
          taskGroup_weekly_target,
          taskGroup_weekly_data,
          taskGroup_monthly_target,
          taskGroup_monthly_completed,
          taskGroup_monthly_percentage,
        };
      });

      return { ...dept, task_groups: processedTaskGroups };
    });

    setDepartments(updatedDepartments);
  }, [weeklyPerformance, weeks, currentWeek, currentISOYear, weekYearMap]);

  const toggleTaskGroup = (id) => {
    setExpandedTaskGroups((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    );
  };

  /* ------------------ UI ------------------ */

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, width: "100%", overflow: "hidden" }}>
      {/* Filters */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {yearOptions.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {moment.months().map((m, i) => (
              <MenuItem key={m} value={i + 1}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{ borderRadius: 3, maxWidth: "100%", overflowX: "auto" }}
      >
        <Table
          sx={{
            minWidth: 1200,
            tableLayout: "fixed",
            "& .MuiTableCell-root": {
              py: 0.75,
            },
          }}
        >
          <TableHead sx={{ backgroundColor: "#0e7c7b" }}>
            <TableRow>
              <TableCell sx={{ width: 50 }} />
              <TableCell sx={{ color: "#fff", width: 150 }}>
                Department
              </TableCell>
              <TableCell sx={{ color: "#fff", width: 180 }}>Goal</TableCell>
              <TableCell sx={{ color: "#fff", width: 150 }}>Owner</TableCell>
              <TableCell align="center" sx={{ color: "#fff", width: 120 }}>
                Target
              </TableCell>

              {weeks.map((w) => (
                <TableCell
                  key={w.key}
                  align="center"
                  sx={{ color: "#fff", width: 100 }}
                >
                  {w.label}
                  <Typography sx={{ fontSize: 9, lineHeight: 1.2 }}>
                    {w.dateRange}
                  </Typography>
                </TableCell>
              ))}

              <TableCell sx={{ color: "#fff", width: 180 }}>
                Achievment
              </TableCell>
              <TableCell sx={{ color: "#fff", width: 100 }}>%</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {departments.map((dept) =>
              dept.task_groups.map((tg) => {
                const tgId = `${dept.department_id}-${tg.normalized_task_name}`;
                const open = expandedTaskGroups.includes(tgId);

                if (tg.users.length === 1) {
                  const u = tg.users[0];
                  return (
                    <TableRow key={u.goal_id} hover>
                      <TableCell />
                      <TableCell>{dept.department_name}</TableCell>
                      <TableCell>{u.goal_name}</TableCell>
                      <TableCell>{u.user.name}</TableCell>
                      <TableCell align="center">{u.weekly_target}</TableCell>

                      {weeks.map((w) => (
                        <TableCell key={w.key} align="center">
                          {(u.weekly_data[w.key] === 0
                            ? ""
                            : u.weekly_data[w.key]) ?? "-"}
                        </TableCell>
                      ))}

                      <TableCell>
                        <Typography variant="body2" sx={{ mb: 0.25 }}>
                          {u.monthly.completed}/{u.monthly.target}
                        </Typography>
                        <LinearProgress
                          value={Math.min(u.monthly.percentage, 100)}
                          variant="determinate"
                          color={getProgressColor(u.monthly.percentage)}
                          sx={{ height: 5, borderRadius: 3 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={`${u.monthly.percentage}%`}
                          color={getProgressColor(u.monthly.percentage)}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  );
                }

                return (
                  <React.Fragment key={tgId}>
                    {/* Parent Row */}
                    <TableRow
                      hover
                      onClick={() => toggleTaskGroup(tgId)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <IconButton size="small">
                          {open ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{dept.department_name}</TableCell>
                      <TableCell>{tg.task_name}</TableCell>
                      <TableCell>{tg.users.length} users</TableCell>
                      <TableCell align="center">
                        {tg.taskGroup_weekly_target}
                      </TableCell>

                      {weeks.map((w) => (
                        <TableCell key={w.key} align="center">
                          {(tg.taskGroup_weekly_data[w.key] === 0
                            ? ""
                            : tg.taskGroup_weekly_data[w.key]) ?? "-"}
                        </TableCell>
                      ))}

                      <TableCell>
                        <Typography variant="body2" sx={{ mb: 0.25 }}>
                          {tg.taskGroup_monthly_completed}/
                          {tg.taskGroup_monthly_target}
                        </Typography>
                        <LinearProgress
                          value={Math.min(tg.taskGroup_monthly_percentage, 100)}
                          variant="determinate"
                          color={getProgressColor(
                            tg.taskGroup_monthly_percentage
                          )}
                          sx={{ height: 5, borderRadius: 3 }}
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={`${tg.taskGroup_monthly_percentage}%`}
                          color={getProgressColor(
                            tg.taskGroup_monthly_percentage
                          )}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>

                    {/* Collapse Row */}
                    <TableRow>
                      <TableCell
                        colSpan={weeks.length + 8}
                        sx={{ p: 0, border: 0 }}
                      >
                        <Collapse in={open} timeout={300} unmountOnExit>
                          <Box sx={{ bgcolor: "#f9f9f9" }}>
                            <Table
                              sx={{
                                tableLayout: "fixed",
                                "& .MuiTableCell-root": {
                                  py: 0.6,
                                  borderBottom: "1px solid #e0e0e0",
                                },
                              }}
                            >
                              <TableBody>
                                {tg.users.map((u) => (
                                  <TableRow key={u.goal_id} hover>
                                    <TableCell sx={{ width: 50 }} />
                                    <TableCell sx={{ width: 150 }} />
                                    <TableCell sx={{ width: 180 }}>
                                      <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ lineHeight: 1.2 }}
                                      >
                                        {u.goal_name}
                                      </Typography>
                                    </TableCell>
                                    <TableCell sx={{ width: 150 }}>
                                      {u.user.name}
                                    </TableCell>
                                    <TableCell
                                      align="center"
                                      sx={{ width: 120 }}
                                    >
                                      {u.weekly_target}
                                    </TableCell>

                                    {weeks.map((w) => (
                                      <TableCell
                                        key={w.key}
                                        align="center"
                                        sx={{ width: 100 }}
                                      >
                                        {(u.weekly_data[w.key] === 0
                                          ? ""
                                          : u.weekly_data[w.key]) ?? "-"}
                                      </TableCell>
                                    ))}

                                    <TableCell sx={{ width: 180 }}>
                                      <Typography
                                        variant="body2"
                                        sx={{ mb: 0.25 }}
                                      >
                                        {u.monthly.completed}/{u.monthly.target}
                                      </Typography>
                                      <LinearProgress
                                        value={Math.min(
                                          u.monthly.percentage,
                                          100
                                        )}
                                        variant="determinate"
                                        color={getProgressColor(
                                          u.monthly.percentage
                                        )}
                                        sx={{ height: 5, borderRadius: 3 }}
                                      />
                                    </TableCell>

                                    <TableCell sx={{ width: 100 }}>
                                      <Chip
                                        size="small"
                                        label={`${u.monthly.percentage}%`}
                                        color={getProgressColor(
                                          u.monthly.percentage
                                        )}
                                      />
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackBarConfig.open}
        autoHideDuration={2000}
        onClose={() => setSnackBarConfig({ ...snackBarConfig, open: false })}
      >
        <Alert severity={snackBarConfig.severity}>
          {snackBarConfig.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default CeoDashboard;
